import { Storage } from '@ionic/storage';
import getLogger from '../logger';

const logger = getLogger('PlayerIdManager');

const PLAYER_ID_KEY = 'analytics_player_id';

/**
 * Manages anonymous player ID for analytics tracking.
 * The player ID persists across sessions and progress resets.
 */
class PlayerIdManager {
  private storage: Storage | null = null;
  private cachedPlayerId: string | null = null;

  /**
   * Initialize the storage system.
   * Must be called before using getPlayerId().
   */
  async init(storage: Storage): Promise<void> {
    try {
      this.storage = storage;
    } catch (error) {
      logger.debug('[Analytics] Failed to initialize PlayerIdManager:', error);
    }
  }

  /**
   * Get or generate the anonymous player ID.
   * Returns cached value if available to avoid repeated storage reads.
   */
  async getPlayerId(): Promise<string> {
    // Return cached value if available
    if (this.cachedPlayerId) {
      return this.cachedPlayerId;
    }

    try {
      if (!this.storage) {
        throw new Error('Storage not initialized. Call init() first.');
      }

      // Try to load existing ID from storage
      let playerId = await this.storage.get(PLAYER_ID_KEY);

      // Generate new ID if none exists
      if (!playerId) {
        playerId = this.generateUUID();
        await this.storage.set(PLAYER_ID_KEY, playerId);
        logger.debug('[Analytics] Generated new player ID:', playerId);
      } else {
        logger.debug('[Analytics] Loaded existing player ID:', playerId);
      }

      this.cachedPlayerId = playerId;
      return playerId;
    } catch (error) {
      logger.debug('[Analytics] Failed to get/generate player ID:', error);
      // Return a fallback UUID that won't be persisted
      return this.generateUUID();
    }
  }

  /**
   * Generate a UUID using the Web Crypto API.
   * Falls back to a simple random string if crypto.randomUUID is not available.
   */
  private generateUUID(): string {
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
    } catch (error) {
      logger.debug('[Analytics] crypto.randomUUID not available:', error);
    }

    // Fallback: simple random UUID-like string
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Clear cached player ID (for testing purposes).
   * Does NOT clear the stored value.
   */
  clearCache(): void {
    this.cachedPlayerId = null;
  }
}

// Export singleton instance
export const playerIdManager = new PlayerIdManager();
